import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import Box from '@mui/material/Box'
import { DatePicker } from '@mui/x-date-pickers'
import { format, parse } from 'date-fns'

import { ConfirmDeleteDialog } from '../../components/ConfirmDeleteDialog'
import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import {
  createSalaryItem,
  deleteSalaryItem,
  getInstructorSalary,
  updateSalaryItem,
} from '../../service/instructor-salary.service'
import { InstructorSalaryResponseDto, SalaryItemDto } from '../../service/types'
import { SalaryItemDialog } from './SalaryItemDialog'
import { SalaryItemFormValues } from './schema'

export const InstructorSalary: FC = () => {
  const { t } = useTranslation()
  const { instructorId } = useParams<{ instructorId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const initialMonth = useMemo(() => {
    const monthParam = searchParams.get('month')
    if (monthParam) {
      const parsed = parse(monthParam, 'yyyy-MM', new Date())
      if (!isNaN(parsed.getTime())) return parsed
    }
    return new Date()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedMonth, setSelectedMonth] = useState<Date>(initialMonth)
  const [salaryData, setSalaryData] = useState<InstructorSalaryResponseDto | null>(null)
  const [loading, setLoading] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<SalaryItemDto | null>(null)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null)

  const monthString = useMemo(() => format(selectedMonth, 'yyyy-MM'), [selectedMonth])

  const loadSalaryData = useCallback(async () => {
    if (!instructorId) return
    setLoading(true)
    try {
      const res = await getInstructorSalary(instructorId, monthString)
      setSalaryData(res.data)
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }, [instructorId, monthString, t])

  useEffect(() => {
    void loadSalaryData()
  }, [loadSalaryData])

  const handleMonthChange = (value: Date | null) => {
    if (value) {
      setSelectedMonth(value)
      setSearchParams({ month: format(value, 'yyyy-MM') }, { replace: true })
    }
  }

  const handleAddItem = () => {
    setEditingItem(null)
    setDialogOpen(true)
  }

  const handleEditItem = (item: SalaryItemDto) => {
    setEditingItem(item)
    setDialogOpen(true)
  }

  const handleDeleteClick = (id: string) => {
    setDeletingItemId(id)
    setDeleteDialogOpen(true)
  }

  const handleSaveItem = async (values: SalaryItemFormValues) => {
    if (!instructorId) return
    try {
      if (editingItem) {
        await updateSalaryItem(editingItem.id, { description: values.description, amount: values.amount })
      } else {
        await createSalaryItem({
          instructorId,
          month: monthString,
          description: values.description,
          amount: values.amount,
        })
      }
      toast.success(t('general.notification.savedSuccessfully'))
      setDialogOpen(false)
      await loadSalaryData()
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    }
  }

  const handleConfirmDelete = async () => {
    if (!deletingItemId) return
    try {
      await deleteSalaryItem(deletingItemId)
      toast.success(t('general.notification.deletedSuccessfully'))
      setDeleteDialogOpen(false)
      setDeletingItemId(null)
      await loadSalaryData()
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    }
  }

  return (
    <ScreenWrapper
      title={salaryData ? salaryData.instructorName : t('instructorSalary.title')}
      filter={
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <DatePicker
            views={['year', 'month']}
            value={selectedMonth}
            onChange={handleMonthChange}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddItem}>
            {t('instructorSalary.addSalaryItem')}
          </Button>
        </Box>
      }
    >
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        {t('instructorSalary.lessons')}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('instructorSalary.table.date')}</TableCell>
              <TableCell>{t('instructorSalary.table.time')}</TableCell>
              <TableCell>{t('instructorSalary.table.lessonType')}</TableCell>
              <TableCell>{t('instructorSalary.table.product')}</TableCell>
              <TableCell align="right">{t('instructorSalary.table.salary')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && salaryData?.lessons.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    {t('instructorSalary.noLessons')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {salaryData?.lessons.map((lesson) => (
              <TableRow key={lesson.id}>
                <TableCell>{lesson.date}</TableCell>
                <TableCell>
                  {lesson.startTime} - {lesson.endTime}
                </TableCell>
                <TableCell>{t(`lessonType.${lesson.lessonType}`)}</TableCell>
                <TableCell>{lesson.productName ?? '-'}</TableCell>
                <TableCell align="right">{lesson.salary.toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        {t('instructorSalary.salaryItems')}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('instructorSalary.table.description')}</TableCell>
              <TableCell align="right">{t('instructorSalary.table.amount')}</TableCell>
              <TableCell align="right">{t('instructorSalary.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && salaryData?.salaryItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t('instructorSalary.noSalaryItems')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {salaryData?.salaryItems.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.description}</TableCell>
                <TableCell align="right">{item.amount.toFixed(2)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEditItem(item)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteClick(item.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {salaryData && (
        <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
            <Typography variant="body1">
              {t('instructorSalary.summary.totalLessons')}: {salaryData.totalLessonsSalary.toFixed(2)}
            </Typography>
            <Typography variant="body1">
              {t('instructorSalary.summary.totalSalaryItems')}: {salaryData.totalSalaryItems.toFixed(2)}
            </Typography>
            <Typography variant="h6">
              {t('instructorSalary.summary.grandTotal')}: {salaryData.grandTotal.toFixed(2)}
            </Typography>
          </Box>
        </Paper>
      )}

      <SalaryItemDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveItem}
        salaryItem={editingItem}
      />
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('general.button.delete')}
        message={t('instructorSalary.deleteConfirm')}
      />
    </ScreenWrapper>
  )
}
